import { email, required, requiredSelect } from '@reliance/forms-js/lib/validation';
import { Facility } from '@reliance/types/models/Facility';
import { HousingUnit } from '@reliance/types/models/HousingUnit';
import { FORM_ERROR } from 'final-form';
import React, { useMemo } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { Form, FormSpy } from 'react-final-form';
import ErrorMessage from '../common/ErrorMessage';
import api from '../common/api';
import StateGroupedSelect from '../facility/StateGroupedSelect';
import { Select } from '../form/Select';
import { TextInput } from '../form/TextInput';
import { useFacilities } from '../hooks/Facility';
import { useMobileDeviceGroups } from '../hooks/MobileDeviceGroup';

type AddEditModalProps = {
  housingUnit?: HousingUnit;
  facility?: Facility;
  onClose: (saved: boolean) => void;
};

type AddEditModalFormValues = {
  id?: number;
  name: string;
  emailAddress: string;
  status: 'Active' | 'Inactive';
  facilityId: number | '';
  mobileDeviceGroupId: number | '' | null;
};

type MobileDeviceGroupFieldProps = {
  facilityId: number | string | '';
};

const MobileDeviceGroupField: React.FC<MobileDeviceGroupFieldProps> = ({ facilityId }) => {
  const fid = useMemo(() => {
    if (facilityId === '' || facilityId == null) {
      return undefined;
    }
    const n = typeof facilityId === 'string' ? Number(facilityId) : facilityId;
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [facilityId]);
  const groupFilters = useMemo(
    () => (fid != null ? { facilityId: fid, page: 1, perPage: 200 } : undefined),
    [fid]
  );
  const groups = useMobileDeviceGroups(groupFilters);
  return (
    <Select label="Mobile Device Group" name="mobileDeviceGroupId">
      <option value="">Select Group</option>
      {(groups.result?.data || []).map((g) => (
        <option key={g.id} value={g.id}>
          {g.name}
        </option>
      ))}
    </Select>
  );
};

const AddEditModal: React.FC<AddEditModalProps> = (props) => {
  const needFacilityPicker = !props.housingUnit && props.facility == null;
  const facilities = useFacilities();
  const facilityList = facilities.result ?? [];

  const initialValues: AddEditModalFormValues = {
    name: props.housingUnit?.name || '',
    emailAddress: props.housingUnit?.emailAddress || '',
    status: props.housingUnit?.status || 'Active',
    facilityId: props.housingUnit?.facility?.id ?? props.facility?.id ?? '',
    mobileDeviceGroupId: props.housingUnit?.mobileDeviceGroup?.id ?? ''
  };

  const onSubmit = async (values: AddEditModalFormValues) => {
    try {
      const payload = {
        name: values.name,
        emailAddress: values.emailAddress,
        status: values.status,
        facilityId: values.facilityId === '' ? null : Number(values.facilityId),
        mobileDeviceGroupId:
          values.mobileDeviceGroupId === '' || values.mobileDeviceGroupId == null
            ? null
            : Number(values.mobileDeviceGroupId)
      };
      if (props.housingUnit) {
        await api.put(`/housing-unit/${props.housingUnit.id}`, payload);
      } else {
        await api.post('/housing-unit', payload);
      }
      props.onClose(true);
      return null;
    } catch (error) {
      return { [FORM_ERROR]: error };
    }
  };

  return (
    <Modal show={true} onHide={() => props.onClose(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {props.housingUnit ? 'Edit' : 'Add'} Housing Unit
        </Modal.Title>
      </Modal.Header>

      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        render={({ handleSubmit, submitting, hasValidationErrors, submitError }) => (
          <form onSubmit={handleSubmit}>
            <Modal.Body className="p-4">
              {submitError && (
                <ErrorMessage
                  error={submitError}
                  httpGenericMessage={submitError.message} />
              )}

              <Row className="g-4">
                {needFacilityPicker && (
                  <Col md={12}>
                    <StateGroupedSelect
                      label="Facility"
                      name="facilityId"
                      facilities={facilityList}
                      emptyLabel="Select Facility"
                      validationRules={[requiredSelect]}
                    />
                  </Col>
                )}
                <Col md={6}>
                  <TextInput
                    label="Name"
                    name="name"
                    validationRules={[required]}
                  />
                </Col>

                <Col md={6}>
                  <Select
                    label="Status"
                    name="status"
                    validationRules={[required]}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Select>
                </Col>

                <Col md={6}>
                  <FormSpy subscription={{ values: true }}>
                    {({ values }) => (
                      <MobileDeviceGroupField facilityId={values.facilityId as number | string | ''} />
                    )}
                  </FormSpy>
                </Col>

                <Col md={6}>
                  <TextInput
                    label="Email Address"
                    name="emailAddress"
                    validationRules={[email]}
                  />
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer className="p-3">
              <Button variant="secondary" onClick={() => props.onClose(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={submitting || hasValidationErrors}>
                {submitting ? 'Saving...' : 'Save'}
              </Button>
            </Modal.Footer>
          </form>
        )}
      />
    </Modal>
  );
};
export default AddEditModal;
